<template>
  <section class="experience" ref="experienceSection">
    <h2 class="experience-title">{{ $t('experiences.title') }}</h2>
    <div class="timeline">
      <div
        v-for="(exp, index) in experiences"
        :key="index"
        class="timeline-item"
        :class="{ 'timeline-item-right': index % 2 !== 0 }"
        :ref="el => experienceItems[index] = el"
      >
        <div class="timeline-content">
          <h3>{{ exp.role }}</h3>
          <p class="company">{{ exp.company }}</p>
          <p class="duration">{{ exp.duration }}</p>
          <div class="tech-stack">
            <span v-for="tech in exp.techStack" :key="tech" class="tech-badge">{{ tech }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const experiences = [
  { 
    role: "Junior AI Engineer", 
    company: "Sunzinet", 
    duration: "2024 - present",
    techStack: ["Python", "ChromaDB", "OpenAI"]
  },
  { 
    role: "Frontend Developer", 
    company: "Sunzinet", 
    duration: "2023 - 2024",
    techStack: ["Vue.js", "JavaScript", "Shopware", "Gradio"]
  },
  { 
    role: "Web Developer", 
    company: "Self Employed", 
    duration: "2022 - 2023",
    techStack: ["React", "Next.js", "Vercel", "Figma"]
  },
  { 
    role: "Junior Frontend Developer", 
    company: "Modelit", 
    duration: "2021 - 2022",
    techStack: ["Nuxt.js", "React", "HTML", "CSS"]
  },
];

const experienceItems = ref([]);
const experienceSection = ref(null);

onMounted(() => {
  // Animate the title
  gsap.from(".experience-title", {
    scrollTrigger: {
      trigger: experienceSection.value,
      start: "top 80%",
    },
    opacity: 0,
    y: 50,
    duration: 1,
    ease: "power3.out",
  });

  // Animate timeline items
  experienceItems.value.forEach((item, index) => {
    gsap.from(item, {
      scrollTrigger: {
        trigger: item,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
      },
      opacity: 0,
      x: index % 2 === 0 ? -100 : 100,
      duration: 1,
      ease: "power3.out",
      delay: index * 0.2,
    });
  });

  // Animate the timeline line
  gsap.from(".timeline::after", {
    scrollTrigger: {
      trigger: experienceSection.value,
      start: "top 80%",
      end: "bottom 20%",
      scrub: true,
    },
    scaleY: 0,
    transformOrigin: "top",
  });
});
</script>

<style scoped>
.experience {
  padding: 100px 20px;
  background-color: #0a0a0a;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.experience-title {
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
}

.experience-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 3px;
  background-color: #ff3366;
}

.timeline {
  width: 100%;
  max-width: 800px;
  position: relative;
  margin: 0 auto;
  padding: 40px 0;
}

.timeline::after {
  content: '';
  position: absolute;
  width: 4px;
  background-color: #ff3366;
  top: 0;
  bottom: 0;
  left: 50%;
  margin-left: -2px;
}

.timeline-item {
  padding: 10px 40px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
}

.timeline-item-right {
  left: 50%;
}

.timeline-content {
  padding: 20px;
  background: #1a1a1a;
  border-radius: 8px;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.timeline-content::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  right: -10px;
  background-color: #ff3366;
  border: 4px solid #0a0a0a;
  top: 15px;
  border-radius: 50%;
  z-index: 1;
}

.timeline-item-right .timeline-content::after {
  left: -10px;
}

.timeline-content h3 {
  margin: 0 0 10px 0;
  color: #ff3366;
  font-size: 1.6rem;
}

.company {
  font-weight: bold;
  margin-bottom: 5px;
}

.duration {
  font-style: italic;
  color: #888;
  margin-bottom: 10px;
}

.tech-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.tech-badge {
  background-color: #2c2c2c;
  color: #ff3366;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  letter-spacing: 0.07rem;
}

@media screen and (max-width: 600px) {
  .timeline::after {
    left: 31px;
  }
  
  .timeline-item {
    width: 100%;
    padding-left: 70px;
    padding-right: 25px;
  }
  
  .timeline-item-right {
    left: 0%;
  }
  
  .timeline-content::after {
    left: -10px;
  }
}
</style>